import {
  filterAvailableProducts,
  getGlobalValue,
  getUpdatedPropertyFeatures,
  getUser,
  isFrench,
} from "./global-util";
import {
  arrayUnique,
  bool as utilBool,
  filterObject,
  getProp,
  inArray,
  isEmpty,
  isNone,
  isObject,
  listSize,
  makeNumericId,
  splitWords,
  strval,
} from "./util";
import { makeAddressLine } from "./oss-util";
import getLogger from "./debug-logger";
import { isCondo } from "./features-config";

const log = getLogger("oss-ms-util", 1);

// transform address to a form expected by microservice
export const buildMsRequestAddress = () => {
  const addrObj = getGlobalValue("ADDRESS_DATA");
  if (!addrObj) {
    log(`building MS request address from`, addrObj); //---------------- log
    throw new Error("address object not available");
  }
  const reqAddr = {
    Line: makeAddressLine(addrObj),
    Municipality: { Name: addrObj.Municipality, StateProvince: { Code: addrObj.Province } },
    PostalCode: addrObj.PostalCode,
  };
  return filterObject(reqAddr);
};

// Read data helpers
const isMissingKey = ((k) => {
  let missingKeys = null;
  return () => {
    if (isNone(missingKeys)) missingKeys = splitWords(getGlobalValue("FEATURES_MISSING"));
    return inArray(missingKeys, k);
  };
})();

const getVal = (obj, k) => {
  const v = getProp(obj, k);
  return isNone(v) || (v === "" && isMissingKey(k)) ? null : v;
};

const getStr = (obj, k) => {
  const v = getVal(obj, k);
  return isNone(v) ? null : strval(v);
};

const getNum = (obj, k) => {
  const v = getVal(obj, k);
  return isNone(v) ? null : Number(v) || 0;
};

const getBool = (obj, k) => {
  const v = getVal(obj, k);
  return isNone(v) ? null : utilBool(v);
};
// end read data helpers

//------------------------------------------------------------
// collect Residential construction features, filter off nulls
//------------------------------------------------------------
export const buildResidentialFeatures = (feats, container = {}) => {
  if (!isObject(feats) || isEmpty(feats)) return container;
  const updFeats = getUpdatedPropertyFeatures();
  if (isEmpty(updFeats)) return container;

  const userFeats = {
    ResidentialBuilding: {
      UserProvidedResidentialConstructionFeatures: {
        ArchitecturalStyleTypeCode: getStr(feats, "ArchitecturalStyleType"),
        AreaUnitOfMeasurementCode: "SquareFeet",

        BathroomConstruction: {
          BathroomCountTypeCode: getStr(feats, "BathroomCount"),
        },
        BedroomConstruction: {
          NumberOfBedrooms: getNum(feats, "NumberOfBedrooms"),
        },
        BuildingStoreyConstruction: {
          StoreyCountTypeCode: getStr(feats, "StoreyCount"),
        },
        CommercialIndicator: getBool(feats, "CommercialIndicator"),
        ExteriorWallConstructions: [
          {
            ExteriorWallTypeCode: getStr(feats, "ExteriorWallType"),
          },
        ],
        FinishedBasement: getBool(feats, "FinishedBasement"),
        FoundationTypeCode: getStr(feats, "FoundationType"),
        Garages: [
          {
            GarageTypeCode: getStr(feats, "GarageType"),
            NumberOfCars: getNum(feats, "GarageNumberOfCars"),
          },
        ],
        KitchenConstruction: {
          KitchenCountTypeCode: getStr(feats, "KitchenCount"),
        },
        OutbuildingPresentCode: getStr(feats, "OutbuildingPresent"),
        PlumbingTypeCode: getStr(feats, "PlumbingType"),
        PrimaryHeatingTypeCode: getStr(feats, "PrimaryHeatingType"),
        RoofConstructions: [
          {
            RoofTypeCode: getStr(feats, "RoofType"),
          },
        ],
        SquareFootage: getNum(feats, "SquareFootage"),
        SwimmingPoolTypeCode: getStr(feats, "SwimmingPoolType"),
        YearBuilt: getNum(feats, "YearBuilt"),
        ViewCode: getStr(feats, "View"),
        WaterfrontCode: getStr(feats, "Waterfront"),
      },
    },
    Property: {
      UserProvidedPropertyFeatures: {
        Utilities: [
          {
            SewageServiceCode: getStr(feats, "SewageType"),
            WaterServiceCode: getStr(feats, "WaterType"),
          },
        ],
        LotSize: getNum(feats, "LotSize"),
        LotDepth: getNum(feats, "LotDepth"),
        LotFrontage: getNum(feats, "LotFrontage"),
      },
    },
  };
  return Object.assign(container, filterObject(userFeats, true));
};

//------------------------------------------------------------
// collect Condo Unit & Building construction features, filter off nulls
//------------------------------------------------------------
export const buildCondoFeatures = (feats, container = {}) => {
  if (!isObject(feats)) return container;
  const updFeats = getUpdatedPropertyFeatures();
  if (isEmpty(updFeats)) return container;

  const unit = {
    MultiResidentialUnit: {
      FloorLevel: getNum(feats, "FloorLevel"),
      Structure: {
        StructureClassCode: "MultiResidential",
        ConstructionFeatures: {
          YearBuilt: getNum(feats, "YearBuilt"),
          NumberOfStoreys: getNum(feats, "NumberOfStoreys"),
        },
      },
      ConstructionFeatures: {
        TotalFloorArea: getNum(feats, "TotalFloorArea"),
        AreaUnitOfMeasurementCode: "SquareFeet",
        BedroomConstruction: {
          NumberOfBedrooms: getNum(feats, "NumberOfBedrooms"),
          NumberOfDens: getNum(feats, "NumberOfDens"),
        },
        BathroomConstruction: {
          BathroomCountTypeCode: getStr(feats, "NumberOfBathrooms"),
        },
      },
    },
  };
  //
  const prop = {
    Property: {
      UserProvidedPropertyFeatures: {
        Utilities: [
          {
            SewageServiceCode: getStr(feats, "SewageType"),
            WaterServiceCode: getStr(feats, "WaterType"),
          },
        ],
        Building: [
          {
            MultiResidentialBuilding: {
              MultiResidentialUnit: {
                ResidentialConstructionFeatures: {
                  TotalFloorArea: getNum(feats, "TotalFloorArea"),
                  AreaUnitOfMeasurementCode: "SquareFeet",
                  ViewCode: getStr(feats, "View"),
                  GarageNumberOfCars: getNum(feats, "GarageNumberOfCars"),
                  Balcony: getBool(feats, "Balcony"),
                  Locker: getBool(feats, "Locker"),
                  BedroomConstruction: {
                    NumberOfBedrooms: getNum(feats, "NumberOfBedrooms"),
                    NumberOfDens: getNum(feats, "NumberOfDens"),
                  },
                  BathroomConstruction: {
                    BathroomCountTypeCode: getStr(feats, "NumberOfBathrooms"),
                  },
                },
                FloorLevel: getNum(feats, "FloorLevel"),
              },
              CommercialConstructionFeatures: {
                YearBuilt: getNum(feats, "YearBuilt"),
                StructureStyleTypeCode: getStr(feats, "MultiResidentialStyleType"),
                NumberOfStoreys: getNum(feats, "NumberOfStoreys"),
                ExteriorWallTypes: [
                  {
                    CommercialExteriorWallTypeCode: getStr(feats, "ExteriorWallType"),
                  },
                ],
                RoofAttributes: {
                  CommercialRoofSurfaces: [
                    {
                      CommercialRoofSurfaceTypeCode: getStr(feats, "RoofSurface"),
                    },
                  ],
                },
                Plumbing: {
                  CommercialPlumbingTypes: [
                    {
                      CommercialPlumbingTypeCode: getStr(feats, "PlumbingType"),
                    },
                  ],
                },
                Parking: {
                  CommercialParking: [
                    {
                      ParkingTypeCode: getStr(feats, "ParkingType"),
                    },
                  ],
                },
              },
              MultiResidentialStyleTypeCode: getStr(feats, "MultiResidentialStyleType"),
            },
          },
        ],
      },
    },
  };

  return Object.assign(container, filterObject(unit, true), filterObject(prop, true));
};

export const buildUserUpdates = () => {
  const propType = getGlobalValue("PROPERTY_TYPE");
  const feats = getUpdatedPropertyFeatures();
  return isCondo(propType) ? buildCondoFeatures(feats) : buildResidentialFeatures(feats);
};

// combine request data with common presets
export const buildOssMsAttributes = (dataObj) => {
  return {
    Username: getUser(),
    RequestorID: makeNumericId(),
    ImageryProvider: [
      {
        ImageryProviderTypeCode: "ILookabout",
      },
      {
        ImageryProviderTypeCode: "Google",
      },
    ],
    ...dataObj,
  };
};

// common OSS microservice request object
export const buildOssMsRequest = (prodList, attrObj = null, userData = null) => {
  if (!listSize(prodList)) {
    log(`no products specified`, prodList); //------- log
    return null;
  }
  prodList = arrayUnique(prodList);
  prodList = filterAvailableProducts(prodList, true);
  if (!listSize(prodList)) {
    log(`requested products not available for this user`); //------- log
  }

  return {
    microservice: "opta-service-bus",
    headers: {
      "Accept-Language": isFrench() ? "fr" : "en",
    },
    params: {
      Products: prodList.join(),
    },
    body: {
      ProductRequest: [
        {
          Address: buildMsRequestAddress(),
          ...(userData || {}),
        },
      ],
      UserAttributes: buildOssMsAttributes(attrObj),
    },
  };
};